<template>
  <div class="w-full min-h-[500px]">
    <div class="pt-4">
      <div class="text-center">
        <div class="my-3">
          <LayupIcon class="my-3" />
          <h5 class="h5 text-grey-light-900 text-lg font-semibold">Sign in to your Layup account</h5>
          <div v-if="errorMessage" class="rounded-md w-full bg-red-100 px-4 py-3 text-sm text-red-700">
            {{ errorMessage }}
          </div>
        </div>
      </div>

      <div v-if="isLoading && userStore.user._id">
        <LayupSpinner />
        <p class="text-grey-light-900 text-sm text-center">Creating your payment plan...</p>
      </div>
      <form class="py-2.5" v-else>
        <MobileNumberInput v-model="form.username" :label="'Mobile Number'" :disabled="isLoading" class="text-left" />

        <PasswordInput v-model="form.password" :disabled="isLoading" :label="`Password`" class="text-left" />

        <div class="flex flex-row my-2">
          <div class="flex-1 text-left text-sm">
            <input type="checkbox" v-model="rememberMe" name="rememberMe" /> <span class="text-black">Remember me</span>
          </div>
          <div class="font-bold">
            <button class="text-semibold text-brand-700 text-sm" @click="onForgotPassword">Forgot password</button>
          </div>
        </div>
        <TurnstileWidget ref="turnstileRef" @success="handleSuccess" />
        <LayupButton
          type="submit"
          :disabled="isButtonLoading || !turnstileToken"
          :isLoading="isButtonLoading"
          @click="onSubmit()"
          :label="`Sign In`"
          data-testid="sign_in_button"
        />

        <div class="text-center mt-2.5">
          <p class="text-grey-light-500 text-sm">
            Don't have an account?
            <button class="btn text-brand-700 text-semibold text-sm" @click="onSignUp">Sign up</button>
          </p>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import LayupButton from '@/components/common/LayupButton.vue';
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import MobileNumberInput from '@/components/common/MobileNumberInput.vue';
import PasswordInput from '@/components/common/PasswordInput.vue';
import TurnstileWidget from '@/components/common/TurnstileWidget.vue';
import LayupIcon from '@/components/icons/LayupIcon.vue';
import { NetworkError } from '@/errors';
import type { IPaymentPlan } from '@/models/paymentPlans';
import { forgotPassword, signIn } from '@/services/auth';
import * as OrderService from '@/services/orders';
import * as PaymentPlanService from '@/services/paymentPlans';
import { useUserStore } from '@/stores/users';
import { useWidgetStore } from '@/stores/widget';
import { parseMobile } from '@/utils/form.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const userStore = useUserStore();
const widgetStore = useWidgetStore();

const route = useRoute();
const router = useRouter();
let orderId = route?.params?.id;

const rememberMe = ref(false);
const errorMessage = ref('');
const isLoading = ref(true);
const isButtonLoading = ref(false);
const turnstileToken = ref('');
const turnstileRef = ref<InstanceType<typeof TurnstileWidget> | null>(null);
const form = {
  username: '',
  password: '',
  rememberMe: false,
};

const handleEmailVerificationFlow = async () => {
  if (widgetStore.order.type === 'REWARDS') {
    if (userStore.user.email && !userStore.user.verifiedEmail) {
      const { redirectTo } = route.query as { redirectTo?: string };
      await forgotPassword(redirectTo!, userStore.user.email, 'EMAIL', turnstileToken.value, true);
      await router.replace({ name: 'OtpVerification' });
    } else if (!userStore.user.email && !userStore.user.verifiedEmail) {
      await router.replace({ name: 'EnterEmail' });
    } else {
      await router.replace({ name: 'Contribute' });
    }
    return;
  }
  await router.replace({ name: 'SelectPaymentMethod' });
};

const createOrder = async () => {
  const response = await OrderService.createOrder(widgetStore.boost.partialOrder);
  widgetStore.order = response;
  orderId = response._id;
  widgetStore.boost.partialOrder = {};
  return response;
};

onMounted(async () => {
  try {
    if (widgetStore.boost.partialOrder.type === 'REWARDS' && userStore.user._id) {
      const response = await createOrder();
      // Update id to be able to continue activating the order
      router.replace({ name: 'WidgetSignIn', params: { id: response._id } });
      return;
    }
    await createPaymentPlan(userStore.user._id!);
    await handleEmailVerificationFlow();
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('sign-in', { status: 'network_error', channel: 'widget' });
      errorMessage.value = 'Network Error. Please try again.';
    } else if (error.response?.status === 401) {
      addMetric('sign-in', { status: 'not_logged_in', channel: 'widget' });
    } else {
      addMetric('sign-in', { status: 'error', channel: 'widget' });
      createException(error);
      errorMessage.value = 'An unexpected error occurred. Please try again.';
    }
  } finally {
    isLoading.value = false;
  }
});

const onSubmit = async () => {
  let signInSuccess = false;
  try {
    errorMessage.value = '';
    isButtonLoading.value = true;
    const sanitizedUsername = parseMobile(form.username.replace(/\s+/g, ''));
    form.username = sanitizedUsername;
    form.rememberMe = rememberMe.value;
    const response = await signIn(form.username, form.password, form.rememberMe, turnstileToken.value);
    userStore.signIn(response, form.rememberMe);
    signInSuccess = true;
    addMetric('sign-in', { status: 'success', channel: 'widget' });
  } catch (error: any) {
    turnstileRef.value?.reset();
    turnstileToken.value = '';
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('sign-in', { status: 'network_error', channel: 'widget' });
      errorMessage.value = 'Network Error. Please try again.';
    } else if (error.response?.status === 400 || error.response?.status === 401) {
      addMetric('sign-in', { status: 'failed', channel: 'widget' });
      errorMessage.value = 'Incorrect sign in credentials';
    } else {
      createException(error);
      addMetric('sign-in', { status: 'error', channel: 'widget' });
      errorMessage.value = error.response.data;
    }
  }
  if (signInSuccess) {
    try {
      if (widgetStore.boost.partialOrder.type === 'REWARDS') {
        const response = await createOrder();
        router.replace({ name: 'WidgetSignIn', params: { id: response._id } });
      }
      await createPaymentPlan(userStore.user._id!);
      await handleEmailVerificationFlow();
      addMetric('sign-in-create-plan', { status: 'success', channel: 'widget' });
    } catch (error) {
      if (error instanceof NetworkError) {
        createWarning(error);
        addMetric('sign-in-create-plan', { status: 'network_error', channel: 'widget' });
        errorMessage.value = 'Network Error. Please try again.';
      } else {
        createException(error);
        addMetric('sign-in-create-plan', { status: 'error', channel: 'widget' });
        errorMessage.value = 'Something went wrong creating your plan. Please try again.';
      }
    }
  }
  isButtonLoading.value = false;
};

const createPaymentPlan = async (benefactorId: string) => {
  let billingDay = 0;
  let quantity = 0;
  if (widgetStore.order.type === 'REWARDS') {
    billingDay = widgetStore.boost.newBoost.billingDay;
    quantity = widgetStore.boost.newBoost.duration;
  } else {
    billingDay = widgetStore.layby.billingDay;
    quantity = widgetStore.layby.duration;
  }
  const paymentPlanRequest = {
    orderId,
    benefactorId,
    quantity,
    automaticBilling: true,
    offset: billingDay - 1,
    frequency: 'MONTHLY',
  } as any;
  const paymentPlan = await PaymentPlanService.createPaymentPlan(paymentPlanRequest);
  widgetStore.plan = paymentPlan as IPaymentPlan;
  widgetStore.amountDue = paymentPlan.depositDue ? paymentPlan.depositDue : 0;
  widgetStore.id = paymentPlan._id ? paymentPlan._id : '';
  widgetStore.payAtReference = paymentPlan.payAtReferenceNumber || 'not found';
};

onUnmounted(() => {
  isLoading.value = false;
  isButtonLoading.value = false;
});

const onSignUp = () => {
  router.replace({ name: 'WidgetSignUp' });
};

const onForgotPassword = () => {
  router.replace({ name: 'WidgetForgotPassword' });
};
function handleSuccess(token: string) {
  turnstileToken.value = token;
}
</script>
